import { useState, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Printer, FileCheck, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import PrintEngine from '../lib/PrintEngine';

/**
 * IssueInvoiceModal
 * Opens when user clicks "Issue Official Invoice" on a paid sale.
 * 1. Shows invoice summary + VAT preview
 * 2. Optional: customer TIN input (required for named business customers)
 * 3. Confirm → POST /api/bir/invoices/{id}/issue-invoice
 * 4. On success: shows BIR number + offers to print
 */
export default function IssueInvoiceModal({ open, onOpenChange, invoice, birSettings, businessInfo, onSuccess }) {
  const [customerTin, setCustomerTin] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [result, setResult] = useState(null);  // { bir_invoice_number, bir_document_label, vat_breakdown, invoice }

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setCustomerTin('');
      setResult(null);
    }
  }, [open]);

  if (!invoice) return null;

  const isVatRegistered = birSettings?.vat_type === 'vat_registered';
  const grandTotal = parseFloat(invoice.grand_total || 0);
  const isCredit = invoice.payment_type === 'credit' || invoice.payment_type === 'partial';
  // Goods seller (RR 10-2019): Sales Invoice for paid sales, Charge Invoice for credit/utang
  const docLabel = isCredit ? 'CHARGE INVOICE' : 'SALES INVOICE';

  // VAT preview (client-side, matches backend logic)
  const vatPreview = (() => {
    if (!isVatRegistered) return null;
    const vatable = grandTotal / 1.12;
    const vat = grandTotal - vatable;
    return { vatable: vatable.toFixed(2), vat: vat.toFixed(2) };
  })();

  const handleIssue = async (acknowledgeReturns = false) => {
    setIssuing(true);
    try {
      const res = await api.post(`/bir/invoices/${invoice.id}/issue-invoice`, {
        customer_tin: customerTin.trim(),
        ...(acknowledgeReturns ? { acknowledge_returns: true } : {}),
      });
      setResult(res.data);
      toast.success(`${res.data.bir_document_label} issued: ${res.data.bir_invoice_number}`);
      if (onSuccess) onSuccess(res.data);

      // Auto-print based on per-document-type 3-state setting (SI or CI)
      // "auto" → print immediately; "ask" → prompt; "disabled" → no print
      const rawSetting = isCredit
        ? (birSettings?.ci_auto_print)
        : (birSettings?.si_auto_print);
      // Normalise: legacy boolean true = "auto", false = "disabled"
      const printMode = rawSetting === true ? 'auto' : rawSetting === false ? 'disabled' : (rawSetting || 'auto');

      if (printMode === 'auto') {
        // Small delay so the success state renders first
        setTimeout(() => _triggerPrint(res.data), 400);
      } else if (printMode === 'ask') {
        // Show confirmation — user decides
        const confirmed = window.confirm(`Print ${isCredit ? 'Charge Invoice' : 'Sales Invoice'} now?`);
        if (confirmed) setTimeout(() => _triggerPrint(res.data), 100);
      }
      // "disabled" → do nothing
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Linked-return guard (2026-07-29): the receipt has a return attached —
      // issuing would print an SI for amounts the customer no longer owes.
      // Cashier verifies and confirms explicitly.
      if (e.response?.status === 409 && detail?.error === 'returns_exist') {
        const confirmed = window.confirm(
          `${detail.message}\n\nIssue the official invoice anyway?`
        );
        if (confirmed) {
          setIssuing(false);
          return handleIssue(true);
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to issue invoice'));
      }
    }
    setIssuing(false);
  };

  const _triggerPrint = (issueResult) => {
    const r = issueResult || result;
    if (!r) return;
    const printData = {
      ...r.invoice,
      bir_invoice_number: r.bir_invoice_number,
      bir_document_label: r.bir_document_label,
    };
    const biz = {
      ...businessInfo,
      business_name: birSettings?.business_name_bir || businessInfo?.business_name || '',
      business_name_bir: birSettings?.business_name_bir || '',
      address: birSettings?.effective_address || birSettings?.bir_address || businessInfo?.address || '',
      phone: birSettings?.effective_phone || birSettings?.bir_phone || businessInfo?.phone || '',
      tin: birSettings?.tin || businessInfo?.tin || '',
      bir_machine_serial: birSettings?.machine_serial || '',
      bir_atp_number: birSettings?.atp_number || '',
      bir_permit_number: birSettings?.bir_permit_number || '',
    };
    PrintEngine.print({
      type: 'official_receipt',
      data: printData,
      format: 'full_page',
      businessInfo: biz,
      docCode: r.invoice?.doc_code || '',
    });
  };

  const handlePrint = () => _triggerPrint(result);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="issue-invoice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>
            <FileCheck size={18} />
            Issue Official Invoice
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            {/* Invoice summary */}
            <div className="rounded-lg border p-3 bg-slate-50 space-y-2 text-sm" style={{ borderColor: 'var(--border-default)' }}>
              <div className="flex justify-between">
                <span className="text-slate-500">Internal Ref.</span>
                <span className="font-mono font-semibold text-slate-700">{invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Customer</span>
                <span className="font-medium">{invoice.customer_name || 'Walk-in'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Payment</span>
                <span>{invoice.payment_method || 'Cash'}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-1" style={{ borderColor: 'var(--border-default)' }}>
                <span className="font-semibold">Total Amount</span>
                <span className="font-bold text-[#1A4D2E] text-base" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatPHP(grandTotal)}
                </span>
              </div>
            </div>

            {/* Document type */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Document Type:</span>
              <Badge className="bg-[#1A4D2E] text-white text-xs">{docLabel}</Badge>
            </div>

            {/* VAT breakdown preview */}
            {isVatRegistered && vatPreview && (
              <div className="rounded-lg border p-3 bg-emerald-50 space-y-1 text-xs" style={{ borderColor: '#a7f3d0' }}>
                <p className="font-semibold text-emerald-800 text-[11px] uppercase tracking-wide mb-2">VAT Breakdown (VAT-Inclusive)</p>
                <div className="flex justify-between text-emerald-700">
                  <span>Vatable Sales (net)</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(vatPreview.vatable)}</span>
                </div>
                <div className="flex justify-between text-emerald-700">
                  <span>VAT (12%)</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(vatPreview.vat)}</span>
                </div>
                <div className="flex justify-between font-semibold text-emerald-900 border-t pt-1" style={{ borderColor: '#a7f3d0' }}>
                  <span>Total</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(grandTotal)}</span>
                </div>
              </div>
            )}

            {/* Customer TIN (optional) */}
            <div>
              <Label className="text-xs text-slate-500">Customer TIN <span className="text-slate-400">(optional — required for business customers ≥ ₱500)</span></Label>
              <Input
                data-testid="customer-tin-input"
                className="mt-1 h-9 text-sm"
                placeholder="e.g. 123-456-789-000"
                value={customerTin}
                onChange={e => setCustomerTin(e.target.value)}
              />
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <span>Once issued, this invoice is locked. Corrections require a void or return document.</span>
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                disabled={issuing}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-issue-invoice-btn"
                className="flex-1 bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                onClick={() => handleIssue()}
                disabled={issuing}
              >
                {issuing
                  ? <><RefreshCw size={13} className="animate-spin mr-1.5" /> Issuing…</>
                  : <><FileCheck size={13} className="mr-1.5" /> Issue {docLabel}</>
                }
              </Button>
            </div>
          </div>
        ) : (
          /* Success state */
          <div className="space-y-4">
            <div className="text-center py-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <FileCheck size={22} className="text-emerald-600" />
              </div>
              <p className="font-semibold text-slate-800">{result.bir_document_label} Issued</p>
              <p className="text-2xl font-bold tracking-widest text-[#1A4D2E] mt-1 font-mono"
                data-testid="bir-invoice-number">
                {result.bir_invoice_number}
              </p>
              <p className="text-xs text-slate-500 mt-1">This number is sequential and non-resettable.</p>
            </div>

            {isVatRegistered && result.vat_breakdown && (
              <div className="rounded-lg border p-3 bg-slate-50 space-y-1 text-xs" style={{ borderColor: 'var(--border-default)' }}>
                <p className="font-semibold text-slate-600 text-[11px] uppercase tracking-wide mb-2">VAT Summary</p>
                <div className="flex justify-between text-slate-600">
                  <span>Vatable Sales</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(result.vat_breakdown.vatable_sales)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>VAT (12%)</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(result.vat_breakdown.vat_amount)}</span>
                </div>
                {result.vat_breakdown.vat_exempt_sales > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>VAT-Exempt</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(result.vat_breakdown.vat_exempt_sales)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Done
              </Button>
              <Button
                data-testid="print-bir-invoice-btn"
                className="flex-1 bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                onClick={handlePrint}
              >
                <Printer size={13} className="mr-1.5" /> Print Invoice
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
